import React, { useContext, useEffect } from "react";

import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";
import {
	BrWidgetOtherConfig,
	ExeFeedWidgetOtherConfig,
	LiveStreamConfigWeb,
	LiveWidgetContainer,
	LsOtherConfig,
} from "@features/live-widgets/shared";
import {
	EventContext,
	OfferStoreContext,
	SportOfferContext,
} from "@gp/components";
import { LiveStreamStatus, ProviderNameEnum } from "@gp/models";
import { SportOffer } from "@gp/offer";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";
import EventViewStore from "@state/stores/offer/event/EventViewStore";
import { AdditionalOfferViewStore } from "@state/stores/offer/shared/AdditionalOfferViewStore";
import classnames from "classnames";
import { observer } from "mobx-react";

export const EventLiveWidget = observer(function EventLiveWidget() {
	const event = useContext(EventContext);
	const sport = useContext(SportOfferContext) as SportOffer;
	const store = useContext(OfferStoreContext) as
		| EventViewStore
		| AdditionalOfferViewStore;

	const liveWidgetStore = useLiveWidgetStore();

	const teamOneName =
		store.lookupsStore.teams.get(event.teamOneId || "")?.name || "";
	const teamTwoName =
		store.lookupsStore.teams.get(event.teamTwoId || "")?.name || "";

	const matchId =
		event.source?.matchId && !event.isOutright
			? event.source.matchId
			: null;
	const widget = liveWidgetStore.findActiveWidget(
		event.id,
		WidgetLocation.EVENT_OFFER
	);

	useEffect(() => {
		if (!event.isLive) {
			return;
		}

		const lsStatus: LiveStreamStatus | null =
			event.liveStreamStatus || null;

		if (lsStatus == LiveStreamStatus.Live) {
			const eventHasLsError = liveWidgetStore.eventsWithLsError.has(
				event?.id || ""
			);
			if (!eventHasLsError) {
				liveWidgetStore.addWidget({
					sport: sport.abrv,
					eventId: event.id,
					teamOneName: teamOneName,
					teamTwoName: teamTwoName,
					location: WidgetLocation.SIDE,
					lsStatus: lsStatus,
				});

				return () => {
					liveWidgetStore.removeWidget(
						event.id,
						WidgetLocation.EVENT_OFFER
					);
				};
			}
		}

		const isDisabled = (function () {
			if (event.isOutright || !event.source?.matchId) {
				return true;
			}

			if (event.source.providerName == ProviderNameEnum.LSports) {
				return (
					!WEBPACK_IS_LS_WIDGET_ENABLED ||
					!LsOtherConfig.supportedSports.includes(sport?.abrv)
				);
			}

			if (event.source.providerName == ProviderNameEnum.BetRadar) {
				return !BrWidgetOtherConfig.supportSports.includes(sport?.abrv);
			}

			if (event.source.providerName == ProviderNameEnum.ExeFeed) {
				return !ExeFeedWidgetOtherConfig.supportSports.includes(
					sport?.abrv
				);
			}

			return false;
		})();

		if (isDisabled) {
			return;
		}
		liveWidgetStore.addWidget({
			sport: sport.abrv,
			provider: event.source!.providerName,
			matchId: matchId,
			eventId: event.id,
			teamOneName: teamOneName,
			teamTwoName: teamTwoName,
			location: WidgetLocation.SIDE,
		});

		return () => {
			liveWidgetStore.removeWidget(event.id, WidgetLocation.EVENT_OFFER);
		};
	}, []);

	useEffect(() => {
		if (event.source?.matchId == null) {
			liveWidgetStore.handleExeFeedSourceRemoved(event.id);
		}
	}, [event.source?.matchId]);

	return (
		<div
			className={classnames("event__stream", {
				"is-active": widget,
				"is-inactive": !widget,
			})}
		>
			{/*
			 * This DOM node has to be present when widget is activated.
			 * This is required by sport radar widget function.
			 */}
			<LiveWidgetContainer
				eventId={event.id}
				location={WidgetLocation.EVENT_OFFER}
				lmtProps={{
					divProps: {
						className: classnames(
							"event__stream__widget widget--stream",
							{
								"widget--betradar":
									widget?.provider ===
									ProviderNameEnum.BetRadar,
								"widget--statscore":
									widget?.provider ===
										ProviderNameEnum.LSports &&
									!widget.isError,
								"widget--exe":
									widget?.provider ===
										ProviderNameEnum.ExeFeed &&
									!widget.isError,
							}
						),
					},
				}}
				lsProps={{
					divProps: {
						className:
							"event__stream__widget widget--stream is-live",
					},
					config: LiveStreamConfigWeb,
				}}
			/>
		</div>
	);
});
